import React from 'react';
import { View, StyleSheet } from 'react-native';
import PropTypes from 'prop-types';

import * as Layout from '@components/screen/Layout';
import SociollaCard from '@components/sociolla/SociollaCard';
import Colors from '@global/colors';
import SociollaText from '@components/sociolla/SociollaText';
import { isWeb } from '@global/screens';

// Captions that nest a SociollaText span must set this on it. SociollaText falls back to 13px
// and a 25px line height when the style carries no fontSize, so a bare span renders larger than
// the caption around it.
export const CAPTION_FONT_SIZE = 11;

// Same thousands separator SociollaText uses for currency, without the prefix.
export const formatCount = (value) =>
  Number(value || 0)
    .toFixed(0)
    .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');

// A failed read must never render as a number: a stray 0 reads as a real figure. Every state
// renders in the same fixed-height slot so nothing below the card moves between them.
const Figure = ({ loading, error, value, unit, currency, valueColor }) => {
  const content = () => {
    if (loading) {
      return <Layout.Loader type="tiny" />;
    }

    if (error) {
      return (
        <SociollaText style={styles.unavailable}>Unavailable</SociollaText>
      );
    }

    // One child, always. SociollaText's currency path does Number(children), and two children,
    // even a number and an empty string, arrive as an array and format as NaN.
    const text = currency
      ? value
      : `${formatCount(value)}${unit ? ` ${unit}` : ''}`;

    // Flattened for the same reason as the label: an array hides fontSize from SociollaText.
    return (
      <SociollaText
        type="bold"
        currency={currency}
        numberOfLines={1}
        ellipsizeMode="tail"
        style={StyleSheet.flatten([
          styles.value,
          currency ? styles.valueCurrency : null,
          valueColor ? { color: valueColor } : null,
        ])}>
        {text}
      </SociollaText>
    );
  };

  return <View style={styles.figureSlot}>{content()}</View>;
};

Figure.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  unit: PropTypes.string,
  currency: PropTypes.bool,
  valueColor: PropTypes.string,
};

const SalesmanMetricCard = ({
  label,
  value,
  unit,
  caption,
  valueColor,
  currency,
  loading,
  error,
  labelLines,
  style,
}) => (
  <SociollaCard style={[styles.card, style]}>
    <SociollaText
      style={StyleSheet.flatten([
        styles.label,
        !isWeb && labelLines > 1
          ? { minHeight: LABEL_LINE_HEIGHT * labelLines }
          : null,
      ])}>
      {label}
    </SociollaText>

    <Figure
      loading={loading}
      error={error}
      value={value}
      unit={unit}
      currency={currency}
      valueColor={valueColor}
    />

    {/* Hidden while loading: the AR caption would read "target unavailable" under a spinner. */}
    {caption && !loading ? (
      <SociollaText style={styles.caption}>{caption}</SociollaText>
    ) : null}
  </SociollaCard>
);

SalesmanMetricCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // Printed after the formatted number, e.g. `min`. Not used with currency.
  unit: PropTypes.string,
  caption: PropTypes.node,
  valueColor: PropTypes.string,
  currency: PropTypes.bool,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  // Label lines to reserve on mobile, set per row by the screen.
  labelLines: PropTypes.number,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

SalesmanMetricCard.defaultProps = {
  value: 0,
  unit: '',
  caption: null,
  valueColor: '',
  currency: false,
  loading: false,
  error: false,
  labelLines: 1,
  style: null,
};

// SociollaText sets lineHeight to fontSize * 1.3 itself, ignoring any lineHeight passed in,
// so 13px lines come out at 16.9. Reserves are sized to that, rounded up.
const lineHeightFor = (fontSize) => Math.ceil(fontSize * 1.3);

const LABEL_FONT_SIZE = 13;
const LABEL_LINE_HEIGHT = lineHeightFor(LABEL_FONT_SIZE);

const VALUE_FONT_SIZE = isWeb ? 26 : 20;
const VALUE_FONT_SIZE_CURRENCY = 20;
const VALUE_MARGIN_TOP = 4;

const styles = StyleSheet.create({
  caption: { color: Colors.grey13, fontSize: CAPTION_FONT_SIZE, marginTop: 6 },
  // marginBottom 0 overrides SociollaCard's 30/20 so the row owns the spacing.
  card: {
    // flexBasis, not minWidth, decides where a line breaks; flexGrow fills the row.
    flexBasis: 100,
    flexGrow: 1,
    marginBottom: 0,
    minWidth: 100,
    padding: 16,
  },
  label: { color: Colors.grey13, fontSize: LABEL_FONT_SIZE },
  figureSlot: {
    alignItems: 'flex-start',
    justifyContent: 'center',
    minHeight: lineHeightFor(VALUE_FONT_SIZE) + VALUE_MARGIN_TOP,
  },
  unavailable: { color: Colors.red0, fontSize: 16 },
  value: {
    color: Colors.textSecondary,
    fontSize: VALUE_FONT_SIZE,
    marginTop: VALUE_MARGIN_TOP,
  },
  valueCurrency: { fontSize: VALUE_FONT_SIZE_CURRENCY },
});

export default SalesmanMetricCard;
